import type { AutomationSettingsView, OwnerReportView } from "../analytics-types";
import type {
  AiHandoffView,
  ConversationAiAnalysis,
  ConversationAiReplyAssist,
  ConversationAiSummary,
} from "../automation-types";
import type {
  AssignConversationResult,
  SessionExpiryView,
  BulkResult,
  ConversationFlowRun,
  ConversationSearchHit,
  InboxConversation,
  LiveConversation,
  ConversationViewer,
  SavedView,
  InboxMessage,
  ReactToMessageResult,
  ChooseInteractiveResult,
  InboxPage,
  HomeConversationSummary,
  CannedReply,
  ConversationMacro,
  MacroRunResult,
  SendPacingStatus,
  StartNewChatResult,
} from "../types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

export type InboxApiMethods = Pick<
  ApiClient,
  | "listLiveConversations"
  | "observeConversation"
  | "sendWhisper"
  | "listConversations"
  | "getHomeConversationSummary"
  | "listConversationMessages"
  | "markConversationRead"
  | "listSavedViews"
  | "createSavedView"
  | "deleteSavedView"
  | "sendConversationPresence"
  | "assignConversation"
  | "requestHuman"
  | "setConversationStatus"
  | "setConversationBot"
  | "setConversationFlowsPause"
  | "listConversationFlowRuns"
  | "startConversationFlow"
  | "stopConversationFlow"
  | "startNewChat"
  | "setConversationPriority"
  | "setConversationFlag"
  | "clearConversationAtRisk"
  | "setContactBlocked"
  | "getOwnerReport"
  | "setOwnerReportEnabled"
  | "getAutomationSettings"
  | "setAutomationSettings"
  | "getSessionExpiry"
  | "setSessionExpiry"
  | "searchConversations"
  | "bulkConversations"
  | "sendConversationMessage"
  | "reactToMessage"
  | "chooseInteractive"
  | "createCannedReply"
  | "deleteCannedReply"
  | "listMacros"
  | "createMacro"
  | "deleteMacro"
  | "runMacro"
  | "getSendPacing"
  | "uploadConversationAttachment"
  | "getConversationAiHandoff"
  | "getConversationAiSummary"
  | "analyzeConversationAi"
  | "askConversationAi"
>;

export function createInboxApi({ request, ws }: HttpTransport): InboxApiMethods {
  return {
    async listLiveConversations(id) {
      return (await request<{ items: LiveConversation[] }>("GET", `${ws(id)}/conversations/live`))
        .items;
    },
    async observeConversation(id, conversationId) {
      await request("POST", `${ws(id)}/conversations/${conversationId}/observe`);
    },
    async sendWhisper(id, conversationId, text) {
      await request("POST", `${ws(id)}/conversations/${conversationId}/whisper`, { text });
    },
    listConversations(id, query = {}) {
      const params = new URLSearchParams();
      if (query.view) params.set("view", query.view);
      if (query.status) params.set("status", query.status);
      if (query.assignee) params.set("assignee", query.assignee);
      if (query.unread) params.set("unread", "1");
      if (query.unansweredOver) params.set("unansweredOver", String(query.unansweredOver));
      if (query.priority) params.set("priority", query.priority);
      if (query.unattended) params.set("unattended", "1");
      if (query.sort) params.set("sort", query.sort);
      if (query.labelId) params.set("labelId", query.labelId);
      if (query.labelIds?.length) params.set("labelIds", query.labelIds.join(","));
      if (query.channelId) params.set("channelId", query.channelId);
      if (query.contactId) params.set("contactId", query.contactId);
      if (query.kind) params.set("kind", query.kind);
      if (query.teamId) params.set("teamId", query.teamId);
      if (query.cursor) params.set("cursor", query.cursor);
      if (query.limit) params.set("limit", String(query.limit));
      const qs = params.size > 0 ? `?${params.toString()}` : "";
      return request<InboxPage<InboxConversation>>("GET", `${ws(id)}/conversations${qs}`);
    },
    getHomeConversationSummary(id) {
      return request<HomeConversationSummary>("GET", `${ws(id)}/conversations/home-summary`);
    },
    listConversationMessages(id, conversationId, query = {}) {
      const params = new URLSearchParams();
      if (query.cursor) params.set("cursor", query.cursor);
      if (query.limit) params.set("limit", String(query.limit));
      const qs = params.size > 0 ? `?${params.toString()}` : "";
      return request<InboxPage<InboxMessage>>(
        "GET",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/messages${qs}`,
      );
    },
    async markConversationRead(id, conversationId) {
      await request("POST", `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/read`);
    },
    async listSavedViews(id) {
      return (await request<{ items: SavedView[] }>("GET", `${ws(id)}/conversations/views`)).items;
    },
    createSavedView(id, input) {
      return request<SavedView>("POST", `${ws(id)}/conversations/views`, input);
    },
    async deleteSavedView(id, viewId) {
      await request("DELETE", `${ws(id)}/conversations/views/${encodeURIComponent(viewId)}`);
    },
    sendConversationPresence(id, conversationId, input) {
      return request<{ others: ConversationViewer[]; softLock: boolean }>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/presence`,
        input,
      );
    },
    async assignConversation(id, conversationId, input) {
      const result = await request<Partial<AssignConversationResult> | undefined>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/assign`,
        input,
      );
      return { warnings: result?.warnings ?? [] };
    },
    startNewChat(id, input) {
      return request<StartNewChatResult>("POST", `${ws(id)}/new-chat`, input);
    },
    async requestHuman(id, conversationId) {
      await request(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/request-human`,
        {},
      );
    },
    async setConversationStatus(id, conversationId, input) {
      await request(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/status`,
        input,
      );
    },
    async setConversationBot(id, conversationId, action, durationMinutes) {
      await request("POST", `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/bot`, {
        action,
        ...(durationMinutes ? { durationMinutes } : {}),
      });
    },
    setConversationFlowsPause(id, conversationId, action, durationMinutes) {
      return request<{ flowsPausedAt: string | null; flowsPausedUntil: string | null }>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/flows-pause`,
        { action, ...(durationMinutes ? { durationMinutes } : {}) },
      );
    },
    async listConversationFlowRuns(id, conversationId) {
      return (
        await request<{ items: ConversationFlowRun[] }>(
          "GET",
          `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/flows`,
        )
      ).items;
    },
    async startConversationFlow(id, conversationId, flowId) {
      await request(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/flows/start`,
        { flowId },
      );
    },
    stopConversationFlow(id, conversationId) {
      return request<{ stopped: number }>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/flows/stop`,
        {},
      );
    },
    async setConversationPriority(id, conversationId, priority) {
      await request(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/priority`,
        { priority },
      );
    },
    async setConversationFlag(id, conversationId, flag, value) {
      await request(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/${flag}`,
        value === undefined ? {} : { value },
      );
    },
    async clearConversationAtRisk(id, conversationId) {
      await request(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/clear-at-risk`,
        {},
      );
    },
    async setContactBlocked(id, contactId, value) {
      await request(
        "POST",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/block`,
        value === undefined ? {} : { value },
      );
    },
    getOwnerReport(id) {
      return request<OwnerReportView>("GET", `${ws(id)}/settings/owner-report`);
    },
    setOwnerReportEnabled(id, enabled) {
      return request<OwnerReportView>("PUT", `${ws(id)}/settings/owner-report`, { enabled });
    },
    getAutomationSettings(id) {
      return request<AutomationSettingsView>("GET", `${ws(id)}/settings/automation`);
    },
    setAutomationSettings(id, input) {
      return request<AutomationSettingsView>("PATCH", `${ws(id)}/settings/automation`, input);
    },
    getSessionExpiry(id) {
      return request<SessionExpiryView>("GET", `${ws(id)}/settings/session-expiry`);
    },
    setSessionExpiry(id, input) {
      return request<SessionExpiryView>("PATCH", `${ws(id)}/settings/session-expiry`, input);
    },
    async searchConversations(id, q) {
      return (
        await request<{ items: ConversationSearchHit[] }>(
          "GET",
          `${ws(id)}/conversations/search?q=${encodeURIComponent(q)}`,
        )
      ).items;
    },
    bulkConversations(id, input) {
      return request<BulkResult>("POST", `${ws(id)}/conversations/bulk`, input);
    },
    async sendConversationMessage(id, conversationId, input) {
      return (
        await request<{ message: InboxMessage }>(
          "POST",
          `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/messages`,
          input,
        )
      ).message;
    },
    reactToMessage(id, conversationId, messageId, emoji) {
      return request<ReactToMessageResult>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/reaction`,
        { emoji },
      );
    },
    chooseInteractive(id, conversationId, messageId, input) {
      return request<ChooseInteractiveResult>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/choice`,
        input,
      );
    },
    createCannedReply: (id, input) =>
      request<CannedReply>("POST", `${ws(id)}/conversations/canned-replies`, input),
    async deleteCannedReply(id, replyId) {
      await request<void>(
        "DELETE",
        `${ws(id)}/conversations/canned-replies/${encodeURIComponent(replyId)}`,
      );
    },
    async listMacros(id) {
      return (await request<{ items: ConversationMacro[] }>("GET", `${ws(id)}/macros`)).items;
    },
    async createMacro(id, input) {
      return (await request<{ macro: ConversationMacro }>("POST", `${ws(id)}/macros`, input)).macro;
    },
    async deleteMacro(id, macroId) {
      await request<void>("DELETE", `${ws(id)}/macros/${encodeURIComponent(macroId)}`);
    },
    runMacro: (id, conversationId, macroId) =>
      request<MacroRunResult>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/macros/${encodeURIComponent(macroId)}/run`,
      ),
    getSendPacing: (id, channelId) =>
      request<SendPacingStatus>(
        "GET",
        `${ws(id)}/broadcasts/send-pacing?channelId=${encodeURIComponent(channelId)}`,
      ),
    async uploadConversationAttachment(id, conversationId, file) {
      const conv = `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/attachments`;
      const declared = await request<{
        attachmentId: string;
        kind: "image" | "document" | "audio" | "video";
      }>("POST", conv, {
        filename: file.name ?? "attachment",
        mimeType: file.type,
        sizeBytes: file.size,
      });
      await request<unknown>(
        "PUT",
        `${conv}/${encodeURIComponent(declared.attachmentId)}`,
        undefined,
        { body: file, contentType: file.type },
      );
      return { attachmentId: declared.attachmentId, kind: declared.kind };
    },
    getConversationAiHandoff: async (id, conversationId) =>
      (
        await request<{ handoff: AiHandoffView | null }>(
          "GET",
          `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/ai-handoff`,
        )
      ).handoff,
    getConversationAiSummary: (id, conversationId, opts = {}) =>
      request<ConversationAiSummary>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/ai-summary`,
        opts,
      ),
    analyzeConversationAi: (id, conversationId, opts = {}) =>
      request<ConversationAiAnalysis>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/ai-analysis`,
        opts,
      ),
    askConversationAi: (id, conversationId, question, locale) =>
      request<ConversationAiReplyAssist>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/ai-reply-assist`,
        { question, ...(locale ? { locale } : {}) },
      ),
  };
}
